import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Keyboard } from 'lucide-react';

/* Editable shortcut list — add more here as features grow */
const SHORTCUTS = [
  { section: 'Global', keys: ['?'], description: 'Open this keyboard shortcuts overlay' },
  { section: 'Global', keys: ['Esc'], description: 'Close any open dialog / overlay' },
  { section: 'Transactions', keys: ['Click row'], description: 'Open edit dialog for that transaction' },
  { section: 'Transactions', keys: ['Shift', 'click'], description: '(browser) Multi-select for checkboxes' },
  { section: 'Reports', keys: ['Click category'], description: 'Expand / collapse' },
  { section: 'Reports', keys: ['Expand all', '/', 'Collapse all'], description: 'Top-right toolbar toggles' },
  { section: 'Deposits', keys: ['Click tenant'], description: 'Open Tenant Detail modal with linked txns' },
  { section: 'Deposits', keys: ['Pencil icon'], description: 'Edit or override any reconcile match' },
];

function isTypingIn(el) {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

/* Global `?` key listener — opens an overlay listing the app's keyboard tricks */
export default function KeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      // Ignore when user is typing
      if (isTypingIn(e.target)) return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const grouped = SHORTCUTS.reduce((acc, s) => {
    (acc[s.section] = acc[s.section] || []).push(s);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="keyboard-shortcuts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="h-4 w-4" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Press <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted text-[10px] font-mono">?</kbd> anywhere to toggle this panel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {Object.entries(grouped).map(([section, items]) => (
            <div key={section}>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">{section}</h4>
              <div className="space-y-1">
                {items.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 text-[12px]" data-testid="shortcut-row">
                    <span className="text-[#5b6773]">{s.description}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {s.keys.map((k, j) => (
                        <kbd key={j} className="px-1.5 py-0.5 rounded border border-border bg-muted text-[10px] font-mono">{k}</kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
